import React, { useEffect, useState, useCallback } from 'react';
import { Sparkles, Check, X as XIcon, Pencil, RefreshCw, Loader2 } from 'lucide-react';
import AppendixWrapper from './common/AppendixWrapper';
import { useAppendix } from '../context/AppendixContext';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../services/supabaseClient';

const APPENDIX_ID = 'catcher';

const layerColor = (layer) => {
    switch (layer) {
        case 'regex': return 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30';
        case 'vector': return 'bg-sky-500/20 text-sky-300 border-sky-500/30';
        case 'llm': return 'bg-fuchsia-500/20 text-fuchsia-300 border-fuchsia-500/30';
        case 'manual': return 'bg-amber-500/20 text-amber-300 border-amber-500/30';
        default: return 'bg-slate-500/20 text-slate-300 border-slate-500/30';
    }
};

const CatcherAppendix = ({ tramiteId, conversationId, onClose }) => {
    const { authFetch } = useAuth();
    const { registerAppendix, closeAppendix, openAppendix } = useAppendix();

    const [fields, setFields] = useState([]);
    const [values, setValues] = useState({}); // {field_name: {current_value, source}}
    const [pending, setPending] = useState({}); // {field_name: [extraction, ...]}
    const [editing, setEditing] = useState({}); // {field_name: string}
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState({}); // {field_name: bool}

    useEffect(() => {
        registerAppendix(APPENDIX_ID, {
            title: 'Catcher',
            icon: Sparkles
        });
    }, [registerAppendix]);

    // Auto-abrir cuando aparece un trámite (no cierra si desaparece para
    // permitir al usuario mantenerlo abierto manualmente).
    useEffect(() => {
        if (tramiteId) openAppendix(APPENDIX_ID);
    }, [tramiteId, openAppendix]);

    // Carga catálogo + valores
    const reload = useCallback(async () => {
        setLoading(true);
        try {
            // Catálogo: siempre, aunque no haya trámite activo
            const fieldsRes = await authFetch('/api/catcher/fields');
            if (fieldsRes.ok) {
                const data = await fieldsRes.json();
                setFields(data.filter(f => f.active));
            }
            // Valores: solo si hay trámite
            if (tramiteId) {
                const valuesRes = await authFetch(`/api/catcher/values/${tramiteId}`);
                if (valuesRes.ok) {
                    const data = await valuesRes.json();
                    const valuesMap = {};
                    (data.values || []).forEach(v => { valuesMap[v.field_name] = v; });
                    const pendingMap = {};
                    (data.pending || []).forEach(p => {
                        if (!pendingMap[p.field_name]) pendingMap[p.field_name] = [];
                        pendingMap[p.field_name].push(p);
                    });
                    setValues(valuesMap);
                    setPending(pendingMap);
                }
            } else {
                setValues({});
                setPending({});
            }
        } catch (e) {
            console.error('[Catcher] reload error', e);
        } finally {
            setLoading(false);
        }
    }, [tramiteId, authFetch]);

    useEffect(() => { reload(); }, [reload]);

    // Realtime: nuevas extracciones para este trámite
    useEffect(() => {
        if (!tramiteId) return;
        const channel = supabase
            .channel(`catcher-${tramiteId}`)
            .on(
                'postgres_changes',
                {
                    event: 'INSERT',
                    schema: 'espuma',
                    table: 'catcher_extractions',
                    filter: `tramite_id=eq.${tramiteId}`
                },
                (payload) => {
                    const row = payload.new;
                    if (row.status === 'accepted') {
                        setValues(prev => ({
                            ...prev,
                            [row.field_name]: {
                                field_name: row.field_name,
                                current_value: row.extracted_value,
                                source: 'auto',
                                source_extraction: row.id
                            }
                        }));
                    } else if (row.status === 'pending') {
                        setPending(prev => {
                            const list = prev[row.field_name] || [];
                            return { ...prev, [row.field_name]: [row, ...list] };
                        });
                    }
                }
            )
            .on(
                'postgres_changes',
                {
                    event: 'UPDATE',
                    schema: 'espuma',
                    table: 'catcher_field_values',
                    filter: `tramite_id=eq.${tramiteId}`
                },
                () => { reload(); }
            )
            .subscribe();
        return () => { supabase.removeChannel(channel); };
    }, [tramiteId, reload]);

    const acceptPending = async (fieldName, extraction) => {
        setBusy(prev => ({ ...prev, [fieldName]: true }));
        try {
            const res = await authFetch(`/api/catcher/values/${tramiteId}/${fieldName}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ extraction_id: extraction.id })
            });
            if (res.ok) {
                setValues(prev => ({
                    ...prev,
                    [fieldName]: {
                        field_name: fieldName,
                        current_value: extraction.extracted_value,
                        source: 'auto',
                        source_extraction: extraction.id
                    }
                }));
                setPending(prev => {
                    const next = { ...prev };
                    delete next[fieldName];
                    return next;
                });
            }
        } finally {
            setBusy(prev => ({ ...prev, [fieldName]: false }));
        }
    };

    const rejectPending = async (fieldName, extraction) => {
        setBusy(prev => ({ ...prev, [fieldName]: true }));
        try {
            const res = await authFetch(`/api/catcher/values/${tramiteId}/${fieldName}/reject`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ extraction_id: extraction.id })
            });
            if (res.ok) {
                setPending(prev => {
                    const list = (prev[fieldName] || []).filter(e => e.id !== extraction.id);
                    const next = { ...prev };
                    if (list.length) next[fieldName] = list; else delete next[fieldName];
                    return next;
                });
            }
        } finally {
            setBusy(prev => ({ ...prev, [fieldName]: false }));
        }
    };

    const saveManual = async (fieldName) => {
        const value = (editing[fieldName] || '').trim();
        if (!value) return;
        setBusy(prev => ({ ...prev, [fieldName]: true }));
        try {
            const res = await authFetch(`/api/catcher/values/${tramiteId}/${fieldName}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ value })
            });
            if (res.ok) {
                setValues(prev => ({
                    ...prev,
                    [fieldName]: {
                        field_name: fieldName,
                        current_value: value,
                        source: 'manual'
                    }
                }));
                setEditing(prev => {
                    const next = { ...prev };
                    delete next[fieldName];
                    return next;
                });
            }
        } finally {
            setBusy(prev => ({ ...prev, [fieldName]: false }));
        }
    };

    const handleClose = () => {
        closeAppendix(APPENDIX_ID);
        if (onClose) onClose();
    };

    return (
        <AppendixWrapper
            id={APPENDIX_ID}
            title="Catcher"
            icon={Sparkles}
            className="w-[400px]"
            onClose={handleClose}
            defaultPosition={{ x: Math.max(20, window.innerWidth - 440), y: 100 }}
            actions={
                <button
                    onClick={reload}
                    title="Recargar"
                    className="p-1.5 hover:bg-slate-800 rounded text-slate-400 hover:text-white"
                >
                    <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
                </button>
            }
        >
            <div className="flex flex-col h-full bg-slate-950 max-h-[600px]">
                <div className="flex-1 overflow-y-auto p-3 space-y-2 custom-scrollbar">
                    {!tramiteId && (
                        <div className="p-3 mb-2 bg-amber-500/10 border border-amber-500/30 rounded-lg text-amber-300 text-[11px]">
                            Sin trámite activo. Los valores extraídos aparecerán aquí cuando abras o crees un trámite.
                        </div>
                    )}

                    {fields.length === 0 && !loading && (
                        <div className="p-4 text-center text-slate-500 text-xs">
                            No hay campos configurados. Configúralos desde Backoffice → Catcher.
                        </div>
                    )}

                    {fields.map(field => {
                        const value = values[field.field_name];
                        const pendings = pending[field.field_name] || [];
                        const isEditing = editing[field.field_name] !== undefined;
                        const isBusy = !!busy[field.field_name];

                        return (
                            <div key={field.id} className="bg-slate-900/60 border border-slate-800 rounded-xl p-3">
                                <div className="flex items-center justify-between mb-1.5">
                                    <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
                                        {field.label}
                                        {field.required && <span className="text-rose-400 ml-1">*</span>}
                                    </span>
                                    {value?.source === 'manual' && (
                                        <span className={`text-[9px] px-1.5 py-0.5 rounded border ${layerColor('manual')}`}>
                                            manual
                                        </span>
                                    )}
                                </div>

                                {/* Valor actual o input de edición */}
                                {isEditing ? (
                                    <div className="flex items-center gap-1">
                                        <input
                                            type="text"
                                            value={editing[field.field_name]}
                                            onChange={(e) => setEditing(prev => ({ ...prev, [field.field_name]: e.target.value }))}
                                            className="flex-1 bg-slate-800 border border-slate-700 rounded px-2 py-1 text-xs text-white"
                                            autoFocus
                                        />
                                        <button
                                            onClick={() => saveManual(field.field_name)}
                                            disabled={isBusy}
                                            className="p-1 text-emerald-400 hover:bg-slate-800 rounded"
                                            title="Guardar"
                                        >
                                            {isBusy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                                        </button>
                                        <button
                                            onClick={() => setEditing(prev => { const n = { ...prev }; delete n[field.field_name]; return n; })}
                                            className="p-1 text-slate-400 hover:bg-slate-800 rounded"
                                            title="Cancelar"
                                        >
                                            <XIcon size={14} />
                                        </button>
                                    </div>
                                ) : (
                                    <div
                                        className="group flex items-center justify-between cursor-pointer"
                                        onClick={() => setEditing(prev => ({ ...prev, [field.field_name]: value?.current_value || '' }))}
                                    >
                                        <span className={`text-sm ${value?.current_value ? 'text-white' : 'text-slate-600 italic'}`}>
                                            {value?.current_value || 'sin datos'}
                                        </span>
                                        <Pencil size={12} className="text-slate-600 opacity-0 group-hover:opacity-100" />
                                    </div>
                                )}

                                {/* Sugerencias pendientes */}
                                {pendings.map(p => (
                                    <div key={p.id} className="mt-2 p-2 bg-slate-800/50 border border-slate-700 rounded-lg">
                                        <div className="flex items-center justify-between mb-1">
                                            <span className={`text-[9px] px-1.5 py-0.5 rounded border ${layerColor(p.layer)}`}>
                                                {p.layer}{p.confidence ? ` · ${(p.confidence * 100).toFixed(0)}%` : ''}
                                            </span>
                                        </div>
                                        <div className="text-xs text-slate-200 font-mono mb-1">{p.extracted_value}</div>
                                        {p.raw_utterance && (
                                            <div className="text-[10px] text-slate-500 italic mb-2 line-clamp-2">"{p.raw_utterance}"</div>
                                        )}
                                        <div className="flex gap-1">
                                            <button
                                                onClick={() => acceptPending(field.field_name, p)}
                                                disabled={isBusy}
                                                className="flex-1 flex items-center justify-center gap-1 px-2 py-1 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 rounded text-[10px] font-bold uppercase tracking-wider"
                                            >
                                                <Check size={11} /> Aceptar
                                            </button>
                                            <button
                                                onClick={() => rejectPending(field.field_name, p)}
                                                disabled={isBusy}
                                                className="flex-1 flex items-center justify-center gap-1 px-2 py-1 bg-rose-500/20 hover:bg-rose-500/30 text-rose-300 rounded text-[10px] font-bold uppercase tracking-wider"
                                            >
                                                <XIcon size={11} /> Rechazar
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        );
                    })}
                </div>
            </div>
        </AppendixWrapper>
    );
};

export default CatcherAppendix;
